import * as i0 from '@angular/core';
import { forwardRef, PLATFORM_ID, Component, Inject, ContentChildren, Input, Output, HostListener, NgModule } from '@angular/core';
import DxDateBox from 'devextreme/ui/date_box';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import * as i1 from 'devextreme-angular/core';
import { DxComponent, DxTemplateHost, WatcherHelper, NestedOptionHost, IterableDifferHelper, DxIntegrationModule, DxTemplateModule } from 'devextreme-angular/core';
import { DxiButtonModule, DxoOptionsModule, DxoCalendarOptionsModule, DxoDisplayFormatModule, DxoDropDownOptionsModule, DxoAnimationModule, DxoHideModule, DxoFromModule, DxoPositionModule, DxoAtModule, DxoBoundaryOffsetModule, DxoCollisionModule, DxoMyModule, DxoOffsetModule, DxoToModule, DxoShowModule } from 'devextreme-angular/ui/nested';
import { DxoDateBoxAnimationModule, DxoDateBoxAtModule, DxoDateBoxBoundaryOffsetModule, DxiDateBoxButtonModule, DxoDateBoxCalendarOptionsModule, DxoDateBoxCollisionModule, DxoDateBoxDisplayFormatModule, DxoDateBoxDropDownOptionsModule, DxoDateBoxFromModule, DxoDateBoxHideModule, DxoDateBoxMyModule, DxoDateBoxOffsetModule, DxoDateBoxOptionsModule, DxoDateBoxPositionModule, DxoDateBoxShowModule, DxoDateBoxToModule, DxiDateBoxToolbarItemModule } from 'devextreme-angular/ui/date-box/nested';
export * from 'devextreme-angular/ui/date-box/nested';
import { PROPERTY_TOKEN_buttons, PROPERTY_TOKEN_toolbarItems } from 'devextreme-angular/core/tokens';

/*!
 * devextreme-angular
 * Version: 25.1.6
 * Build date: Mon Oct 13 2025
 *
 * Copyright (c) 2012 - 2025 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */
/* tslint:disable:max-line-length */
const CUSTOM_VALUE_ACCESSOR_PROVIDER = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DxDateBoxComponent),
  multi: true
};
/**
 * The DateBox is a UI component that displays date and time in a specified format, and enables a user to pick or type in the required date/time value.

 */
let DxDateBoxComponent = /*#__PURE__*/(() => {
  class DxDateBoxComponent extends DxComponent {
    _watcherHelper;
    _idh;
    set _buttonsContentChildren(value) {
      this.setChildren('buttons', value);
    }
    set _toolbarItemsContentChildren(value) {
      this.setChildren('toolbarItems', value);
    }
    instance = null;
    /**
     * Specifies whether or not the UI component allows an end user to enter a custom value.
    
     */
    get acceptCustomValue() {
      return this._getOption('acceptCustomValue');
    }
    set acceptCustomValue(value) {
      this._setOption('acceptCustomValue', value);
    }
    /**
     * Specifies the shortcut key that sets focus on the UI component.
    
     */
    get accessKey() {
      return this._getOption('accessKey');
    }
    set accessKey(value) {
      this._setOption('accessKey', value);
    }
    /**
     * Specifies whether the UI component changes its visual state as a result of user interaction.
    
     */
    get activeStateEnabled() {
      return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value) {
      this._setOption('activeStateEnabled', value);
    }
    /**
     * Specifies whether or not adaptive UI component rendering is enabled on a small screen.
    
     */
    get adaptivityEnabled() {
      return this._getOption('adaptivityEnabled');
    }
    set adaptivityEnabled(value) {
      this._setOption('adaptivityEnabled', value);
    }
    /**
     * Specifies the Apply button&apos;s text.
    
     */
    get applyButtonText() {
      return this._getOption('applyButtonText');
    }
    set applyButtonText(value) {
      this._setOption('applyButtonText', value);
    }
    /**
     * Specifies the way an end user applies the selected value.
    
     */
    get applyValueMode() {
      return this._getOption('applyValueMode');
    }
    set applyValueMode(value) {
      this._setOption('applyValueMode', value);
    }
    /**
     * Allows you to add custom buttons to the input text field.
    
     */
    get buttons() {
      return this._getOption('buttons');
    }
    set buttons(value) {
      this._setOption('buttons', value);
    }
    /**
     * Configures the calendar&apos;s value picker.
    
     */
    get calendarOptions() {
      return this._getOption('calendarOptions');
    }
    set calendarOptions(value) {
      this._setOption('calendarOptions', value);
    }
    /**
     * Specifies the Cancel button&apos;s text.
    
     */
    get cancelButtonText() {
      return this._getOption('cancelButtonText');
    }
    set cancelButtonText(value) {
      this._setOption('cancelButtonText', value);
    }
    /**
     * Specifies the message displayed if the specified date is later than the max value or earlier than the min value.
    
     */
    get dateOutOfRangeMessage() {
      return this._getOption('dateOutOfRangeMessage');
    }
    set dateOutOfRangeMessage(value) {
      this._setOption('dateOutOfRangeMessage', value);
    }
    /**
     * Specifies the date value serialization format.
    
     */
    get dateSerializationFormat() {
      return this._getOption('dateSerializationFormat');
    }
    set dateSerializationFormat(value) {
      this._setOption('dateSerializationFormat', value);
    }
    /**
     * Specifies whether to render the drop-down field&apos;s content when it is displayed. If false, the content is rendered immediately.
    
     */
    get deferRendering() {
      return this._getOption('deferRendering');
    }
    set deferRendering(value) {
      this._setOption('deferRendering', value);
    }
    /**
     * Specifies whether the UI component responds to user interaction.
    
     */
    get disabled() {
      return this._getOption('disabled');
    }
    set disabled(value) {
      this._setOption('disabled', value);
    }
    /**
     * Specifies dates that users cannot select. Applies only if pickerType is &apos;calendar&apos;.
    
     */
    get disabledDates() {
      return this._getOption('disabledDates');
    }
    set disabledDates(value) {
      this._setOption('disabledDates', value);
    }
    /**
     * Specifies the date&apos;s display format.
    
     */
    get displayFormat() {
      return this._getOption('displayFormat');
    }
    set displayFormat(value) {
      this._setOption('displayFormat', value);
    }
    /**
     * Specifies a custom template for the drop-down button.
    
     */
    get dropDownButtonTemplate() {
      return this._getOption('dropDownButtonTemplate');
    }
    set dropDownButtonTemplate(value) {
      this._setOption('dropDownButtonTemplate', value);
    }
    /**
     * Configures the drop-down that holds the content.
    
     */
    get dropDownOptions() {
      return this._getOption('dropDownOptions');
    }
    set dropDownOptions(value) {
      this._setOption('dropDownOptions', value);
    }
    /**
     * Specifies the global attributes to be attached to the UI component&apos;s container element.
    
     */
    get elementAttr() {
      return this._getOption('elementAttr');
    }
    set elementAttr(value) {
      this._setOption('elementAttr', value);
    }
    /**
     * Specifies whether the UI component can be focused using keyboard navigation.
    
     */
    get focusStateEnabled() {
      return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value) {
      this._setOption('focusStateEnabled', value);
    }
    /**
     * Specifies the UI component&apos;s height.
    
     */
    get height() {
      return this._getOption('height');
    }
    set height(value) {
      this._setOption('height', value);
    }
    /**
     * Specifies text for a hint that appears when a user pauses on the UI component.
    
     */
    get hint() {
      return this._getOption('hint');
    }
    set hint(value) {
      this._setOption('hint', value);
    }
    /**
     * Specifies whether the UI component changes its state when a user pauses on it.
    
     */
    get hoverStateEnabled() {
      return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value) {
      this._setOption('hoverStateEnabled', value);
    }
    /**
     * Specifies the attributes to be passed on to the underlying HTML element.
    
     */
    get inputAttr() {
      return this._getOption('inputAttr');
    }
    set inputAttr(value) {
      this._setOption('inputAttr', value);
    }
    /**
     * Specifies the interval between neighboring values in the popup list in minutes.
    
     */
    get interval() {
      return this._getOption('interval');
    }
    set interval(value) {
      this._setOption('interval', value);
    }
    /**
     * Specifies the message displayed if the typed value is not a valid date or time.
    
     */
    get invalidDateMessage() {
      return this._getOption('invalidDateMessage');
    }
    set invalidDateMessage(value) {
      this._setOption('invalidDateMessage', value);
    }
    /**
     * Specifies whether the component&apos;s current value differs from the initial value.
    
     */
    get isDirty() {
      return this._getOption('isDirty');
    }
    set isDirty(value) {
      this._setOption('isDirty', value);
    }
    /**
     * Specifies or indicates whether the editor&apos;s value is valid.
    
     */
    get isValid() {
      return this._getOption('isValid');
    }
    set isValid(value) {
      this._setOption('isValid', value);
    }
    /**
     * Specifies a text string used to annotate the editor&apos;s value.
    
     */
    get label() {
      return this._getOption('label');
    }
    set label(value) {
      this._setOption('label', value);
    }
    /**
     * Specifies the label&apos;s display mode.
    
     */
    get labelMode() {
      return this._getOption('labelMode');
    }
    set labelMode(value) {
      this._setOption('labelMode', value);
    }
    /**
     * The latest date that can be selected in the UI component.
    
     */
    get max() {
      return this._getOption('max');
    }
    set max(value) {
      this._setOption('max', value);
    }
    /**
     * Specifies the maximum number of characters you can enter into the textbox.
    
     */
    get maxLength() {
      return this._getOption('maxLength');
    }
    set maxLength(value) {
      this._setOption('maxLength', value);
    }
    /**
     * The earliest date that can be selected in the UI component.
    
     */
    get min() {
      return this._getOption('min');
    }
    set min(value) {
      this._setOption('min', value);
    }
    /**
     * The value to be assigned to the `name` attribute of the underlying HTML element.
    
     */
    get name() {
      return this._getOption('name');
    }
    set name(value) {
      this._setOption('name', value);
    }
    /**
     * Specifies whether or not the drop-down editor is displayed.
    
     */
    get opened() {
      return this._getOption('opened');
    }
    set opened(value) {
      this._setOption('opened', value);
    }
    /**
     * Specifies whether a user can open the drop-down list by clicking a text field.
    
     */
    get openOnFieldClick() {
      return this._getOption('openOnFieldClick');
    }
    set openOnFieldClick(value) {
      this._setOption('openOnFieldClick', value);
    }
    /**
     * Specifies the type of the date/time picker.
    
     */
    get pickerType() {
      return this._getOption('pickerType');
    }
    set pickerType(value) {
      this._setOption('pickerType', value);
    }
    /**
     * Specifies a placeholder for the input field.
    
     */
    get placeholder() {
      return this._getOption('placeholder');
    }
    set placeholder(value) {
      this._setOption('placeholder', value);
    }
    /**
     * Specifies whether the editor is read-only.
    
     */
    get readOnly() {
      return this._getOption('readOnly');
    }
    set readOnly(value) {
      this._setOption('readOnly', value);
    }
    /**
     * Switches the UI component to a right-to-left representation.
    
     */
    get rtlEnabled() {
      return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value) {
      this._setOption('rtlEnabled', value);
    }
    /**
     * Specifies whether to show the analog clock in the value picker. Applies only if type is &apos;datetime&apos; and pickerType is &apos;calendar&apos;.
    
     */
    get showAnalogClock() {
      return this._getOption('showAnalogClock');
    }
    set showAnalogClock(value) {
      this._setOption('showAnalogClock', value);
    }
    /**
     * Specifies whether to display the Clear button in the UI component.
    
     */
    get showClearButton() {
      return this._getOption('showClearButton');
    }
    set showClearButton(value) {
      this._setOption('showClearButton', value);
    }
    /**
     * Specifies whether the drop-down button is visible.
    
     */
    get showDropDownButton() {
      return this._getOption('showDropDownButton');
    }
    set showDropDownButton(value) {
      this._setOption('showDropDownButton', value);
    }
    /**
     * Specifies whether or not the UI component checks the inner text for spelling mistakes.
    
     */
    get spellcheck() {
      return this._getOption('spellcheck');
    }
    set spellcheck(value) {
      this._setOption('spellcheck', value);
    }
    /**
     * Specifies how the UI component&apos;s text field is styled.
    
     */
    get stylingMode() {
      return this._getOption('stylingMode');
    }
    set stylingMode(value) {
      this._setOption('stylingMode', value);
    }
    /**
     * Specifies the number of the element when the Tab key is used for navigating.
    
     */
    get tabIndex() {
      return this._getOption('tabIndex');
    }
    set tabIndex(value) {
      this._setOption('tabIndex', value);
    }
    /**
     * The read-only property that stores the text displayed by the UI component input element.
    
     */
    get text() {
      return this._getOption('text');
    }
    set text(value) {
      this._setOption('text', value);
    }
    /**
     * Specified the Today button&apos;s text.
    
     */
    get todayButtonText() {
      return this._getOption('todayButtonText');
    }
    set todayButtonText(value) {
      this._setOption('todayButtonText', value);
    }
    /**
     * A format used to display date/time information.
    
     */
    get type() {
      return this._getOption('type');
    }
    set type(value) {
      this._setOption('type', value);
    }
    /**
     * Specifies whether to use an input mask based on the displayFormat property.
    
     */
    get useMaskBehavior() {
      return this._getOption('useMaskBehavior');
    }
    set useMaskBehavior(value) {
      this._setOption('useMaskBehavior', value);
    }
    /**
     * Information on the broken validation rule. Contains the first item from the validationErrors array.
    
     */
    get validationError() {
      return this._getOption('validationError');
    }
    set validationError(value) {
      this._setOption('validationError', value);
    }
    /**
     * An array of validation errors.
    
     */
    get validationErrors() {
      return this._getOption('validationErrors');
    }
    set validationErrors(value) {
      this._setOption('validationErrors', value);
    }
    /**
     * Specifies how the message about the validation rules that are not satisfied by this editor&apos;s value is displayed.
    
     */
    get validationMessageMode() {
      return this._getOption('validationMessageMode');
    }
    set validationMessageMode(value) {
      this._setOption('validationMessageMode', value);
    }
    /**
     * Specifies the position of a validation message relative to the component. The validation message describes the validation rules that this component&apos;s value does not satisfy.
    
     */
    get validationMessagePosition() {
      return this._getOption('validationMessagePosition');
    }
    set validationMessagePosition(value) {
      this._setOption('validationMessagePosition', value);
    }
    /**
     * Indicates or specifies the current validation status.
    
     */
    get validationStatus() {
      return this._getOption('validationStatus');
    }
    set validationStatus(value) {
      this._setOption('validationStatus', value);
    }
    /**
     * Specifies the currently selected date and time.
    
     */
    get value() {
      return this._getOption('value');
    }
    set value(value) {
      this._setOption('value', value);
    }
    /**
     * Specifies the DOM events after which the UI component&apos;s value should be updated.
    
     */
    get valueChangeEvent() {
      return this._getOption('valueChangeEvent');
    }
    set valueChangeEvent(value) {
      this._setOption('valueChangeEvent', value);
    }
    /**
     * Specifies whether the UI component is visible.
    
     */
    get visible() {
      return this._getOption('visible');
    }
    set visible(value) {
      this._setOption('visible', value);
    }
    /**
     * Specifies the UI component&apos;s width.
    
     */
    get width() {
      return this._getOption('width');
    }
    set width(value) {
      this._setOption('width', value);
    }
    /**
    
     * A function that is executed when the UI component loses focus after the text field&apos;s content was changed using the keyboard.
    
    
     */
    onChange;
    /**
    
     * A function that is executed once the drop-down editor is closed.
    
    
     */
    onClosed;
    /**
    
     * A function that is executed when the UI component is rendered and each time the component is repainted.
    
    
     */
    onContentReady;
    /**
    
     * A function that is executed when the UI component&apos;s input has been copied.
    
    
     */
    onCopy;
    /**
    
     * A function that is executed when the UI component&apos;s input has been cut.
    
    
     */
    onCut;
    /**
    
     * A function that is executed before the UI component is disposed of.
    
    
     */
    onDisposing;
    /**
    
     * A function that is executed when the Enter key has been pressed while the UI component is focused.
    
    
     */
    onEnterKey;
    /**
    
     * A function that is executed when the UI component gets focus.
    
    
     */
    onFocusIn;
    /**
    
     * A function that is executed when the UI component loses focus.
    
    
     */
    onFocusOut;
    /**
    
     * A function used in JavaScript frameworks to save the UI component instance.
    
    
     */
    onInitialized;
    /**
    
     * A function that is executed each time the UI component&apos;s input is changed while the UI component is focused.
    
    
     */
    onInput;
    /**
    
     * A function that is executed when a user is pressing a key on the keyboard.
    
    
     */
    onKeyDown;
    /**
    
     * A function that is executed when a user releases a key on the keyboard.
    
    
     */
    onKeyUp;
    /**
    
     * A function that is executed once the drop-down editor is opened.
    
    
     */
    onOpened;
    /**
    
     * A function that is executed after a UI component property is changed.
    
    
     */
    onOptionChanged;
    /**
    
     * A function that is executed when the UI component&apos;s input has been pasted.
    
    
     */
    onPaste;
    /**
    
     * A function that is executed after the UI component&apos;s value is changed.
    
    
     */
    onValueChanged;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    acceptCustomValueChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    accessKeyChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    activeStateEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    adaptivityEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    applyButtonTextChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    applyValueModeChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    buttonsChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    calendarOptionsChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    cancelButtonTextChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    dateOutOfRangeMessageChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    dateSerializationFormatChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    deferRenderingChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    disabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    disabledDatesChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    displayFormatChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    dropDownButtonTemplateChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    dropDownOptionsChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    elementAttrChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    focusStateEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    heightChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    hintChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    hoverStateEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    inputAttrChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    intervalChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    invalidDateMessageChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    isDirtyChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    isValidChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    labelChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    labelModeChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    maxChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    maxLengthChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    minChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    nameChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    openedChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    openOnFieldClickChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    pickerTypeChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    placeholderChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    readOnlyChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    rtlEnabledChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    showAnalogClockChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    showClearButtonChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    showDropDownButtonChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    spellcheckChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    stylingModeChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    tabIndexChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    textChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    todayButtonTextChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    typeChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    useMaskBehaviorChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    validationErrorChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    validationErrorsChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    validationMessageModeChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    validationMessagePositionChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    validationStatusChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    valueChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    valueChangeEventChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    visibleChange;
    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    widthChange;
    /**
    
     * 
    
    
     */
    onBlur;
    change(_) {}
    touched = _ => {};
    constructor(elementRef, ngZone, templateHost, _watcherHelper, _idh, optionHost, transferState, platformId) {
      super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);
      this._watcherHelper = _watcherHelper;
      this._idh = _idh;
      this._createEventEmitters([{
        subscribe: 'change',
        emit: 'onChange'
      }, {
        subscribe: 'closed',
        emit: 'onClosed'
      }, {
        subscribe: 'contentReady',
        emit: 'onContentReady'
      }, {
        subscribe: 'copy',
        emit: 'onCopy'
      }, {
        subscribe: 'cut',
        emit: 'onCut'
      }, {
        subscribe: 'disposing',
        emit: 'onDisposing'
      }, {
        subscribe: 'enterKey',
        emit: 'onEnterKey'
      }, {
        subscribe: 'focusIn',
        emit: 'onFocusIn'
      }, {
        subscribe: 'focusOut',
        emit: 'onFocusOut'
      }, {
        subscribe: 'initialized',
        emit: 'onInitialized'
      }, {
        subscribe: 'input',
        emit: 'onInput'
      }, {
        subscribe: 'keyDown',
        emit: 'onKeyDown'
      }, {
        subscribe: 'keyUp',
        emit: 'onKeyUp'
      }, {
        subscribe: 'opened',
        emit: 'onOpened'
      }, {
        subscribe: 'optionChanged',
        emit: 'onOptionChanged'
      }, {
        subscribe: 'paste',
        emit: 'onPaste'
      }, {
        subscribe: 'valueChanged',
        emit: 'onValueChanged'
      }, {
        emit: 'acceptCustomValueChange'
      }, {
        emit: 'accessKeyChange'
      }, {
        emit: 'activeStateEnabledChange'
      }, {
        emit: 'adaptivityEnabledChange'
      }, {
        emit: 'applyButtonTextChange'
      }, {
        emit: 'applyValueModeChange'
      }, {
        emit: 'buttonsChange'
      }, {
        emit: 'calendarOptionsChange'
      }, {
        emit: 'cancelButtonTextChange'
      }, {
        emit: 'dateOutOfRangeMessageChange'
      }, {
        emit: 'dateSerializationFormatChange'
      }, {
        emit: 'deferRenderingChange'
      }, {
        emit: 'disabledChange'
      }, {
        emit: 'disabledDatesChange'
      }, {
        emit: 'displayFormatChange'
      }, {
        emit: 'dropDownButtonTemplateChange'
      }, {
        emit: 'dropDownOptionsChange'
      }, {
        emit: 'elementAttrChange'
      }, {
        emit: 'focusStateEnabledChange'
      }, {
        emit: 'heightChange'
      }, {
        emit: 'hintChange'
      }, {
        emit: 'hoverStateEnabledChange'
      }, {
        emit: 'inputAttrChange'
      }, {
        emit: 'intervalChange'
      }, {
        emit: 'invalidDateMessageChange'
      }, {
        emit: 'isDirtyChange'
      }, {
        emit: 'isValidChange'
      }, {
        emit: 'labelChange'
      }, {
        emit: 'labelModeChange'
      }, {
        emit: 'maxChange'
      }, {
        emit: 'maxLengthChange'
      }, {
        emit: 'minChange'
      }, {
        emit: 'nameChange'
      }, {
        emit: 'openedChange'
      }, {
        emit: 'openOnFieldClickChange'
      }, {
        emit: 'pickerTypeChange'
      }, {
        emit: 'placeholderChange'
      }, {
        emit: 'readOnlyChange'
      }, {
        emit: 'rtlEnabledChange'
      }, {
        emit: 'showAnalogClockChange'
      }, {
        emit: 'showClearButtonChange'
      }, {
        emit: 'showDropDownButtonChange'
      }, {
        emit: 'spellcheckChange'
      }, {
        emit: 'stylingModeChange'
      }, {
        emit: 'tabIndexChange'
      }, {
        emit: 'textChange'
      }, {
        emit: 'todayButtonTextChange'
      }, {
        emit: 'typeChange'
      }, {
        emit: 'useMaskBehaviorChange'
      }, {
        emit: 'validationErrorChange'
      }, {
        emit: 'validationErrorsChange'
      }, {
        emit: 'validationMessageModeChange'
      }, {
        emit: 'validationMessagePositionChange'
      }, {
        emit: 'validationStatusChange'
      }, {
        emit: 'valueChange'
      }, {
        emit: 'valueChangeEventChange'
      }, {
        emit: 'visibleChange'
      }, {
        emit: 'widthChange'
      }, {
        emit: 'onBlur'
      }]);
      this._idh.setHost(this);
      optionHost.setHost(this);
    }
    _createInstance(element, options) {
      return new DxDateBox(element, options);
    }
    writeValue(value) {
      this.eventHelper.lockedValueChangeEvent = true;
      this.value = value;
      this.eventHelper.lockedValueChangeEvent = false;
    }
    setDisabledState(isDisabled) {
      this.disabled = isDisabled;
    }
    registerOnChange(fn) {
      this.change = fn;
    }
    registerOnTouched(fn) {
      this.touched = fn;
    }
    _createWidget(element) {
      super._createWidget(element);
      this.instance.on('focusOut', e => {
        this.eventHelper.fireNgEvent('onBlur', [e]);
      });
    }
    ngOnDestroy() {
      this._destroyWidget();
    }
    ngOnChanges(changes) {
      super.ngOnChanges(changes);
      this.setupChanges('buttons', changes);
      this.setupChanges('disabledDates', changes);
      this.setupChanges('validationErrors', changes);
    }
    setupChanges(prop, changes) {
      if (!(prop in this._optionsToUpdate)) {
        this._idh.setup(prop, changes);
      }
    }
    ngDoCheck() {
      this._idh.doCheck('buttons');
      this._idh.doCheck('disabledDates');
      this._idh.doCheck('validationErrors');
      this._watcherHelper.checkWatchers();
      super.ngDoCheck();
      super.clearChangedOptions();
    }
    _setOption(name, value) {
      let isSetup = this._idh.setupSingle(name, value);
      let isChanged = this._idh.getChanges(name, value) !== null;
      if (isSetup || isChanged) {
        super._setOption(name, value);
      }
    }
    /** @nocollapse */
    static ɵfac = function DxDateBoxComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxDateBoxComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(i1.DxTemplateHost), i0.ɵɵdirectiveInject(i1.WatcherHelper), i0.ɵɵdirectiveInject(i1.IterableDifferHelper), i0.ɵɵdirectiveInject(i1.NestedOptionHost), i0.ɵɵdirectiveInject(i0.TransferState), i0.ɵɵdirectiveInject(PLATFORM_ID));
    };
    /** @nocollapse */
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: DxDateBoxComponent,
      selectors: [["dx-date-box"]],
      contentQueries: function DxDateBoxComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, PROPERTY_TOKEN_buttons, 4);
          i0.ɵɵcontentQuery(dirIndex, PROPERTY_TOKEN_toolbarItems, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._buttonsContentChildren = _t);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._toolbarItemsContentChildren = _t);
        }
      },
      hostAttrs: ["ngSkipHydration", "true"],
      hostBindings: function DxDateBoxComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("valueChange", function DxDateBoxComponent_valueChange_HostBindingHandler($event) {
            return ctx.change($event);
          })("onBlur", function DxDateBoxComponent_onBlur_HostBindingHandler($event) {
            return ctx.touched($event);
          });
        }
      },
      inputs: {
        acceptCustomValue: "acceptCustomValue",
        accessKey: "accessKey",
        activeStateEnabled: "activeStateEnabled",
        adaptivityEnabled: "adaptivityEnabled",
        applyButtonText: "applyButtonText",
        applyValueMode: "applyValueMode",
        buttons: "buttons",
        calendarOptions: "calendarOptions",
        cancelButtonText: "cancelButtonText",
        dateOutOfRangeMessage: "dateOutOfRangeMessage",
        dateSerializationFormat: "dateSerializationFormat",
        deferRendering: "deferRendering",
        disabled: "disabled",
        disabledDates: "disabledDates",
        displayFormat: "displayFormat",
        dropDownButtonTemplate: "dropDownButtonTemplate",
        dropDownOptions: "dropDownOptions",
        elementAttr: "elementAttr",
        focusStateEnabled: "focusStateEnabled",
        height: "height",
        hint: "hint",
        hoverStateEnabled: "hoverStateEnabled",
        inputAttr: "inputAttr",
        interval: "interval",
        invalidDateMessage: "invalidDateMessage",
        isDirty: "isDirty",
        isValid: "isValid",
        label: "label",
        labelMode: "labelMode",
        max: "max",
        maxLength: "maxLength",
        min: "min",
        name: "name",
        opened: "opened",
        openOnFieldClick: "openOnFieldClick",
        pickerType: "pickerType",
        placeholder: "placeholder",
        readOnly: "readOnly",
        rtlEnabled: "rtlEnabled",
        showAnalogClock: "showAnalogClock",
        showClearButton: "showClearButton",
        showDropDownButton: "showDropDownButton",
        spellcheck: "spellcheck",
        stylingMode: "stylingMode",
        tabIndex: "tabIndex",
        text: "text",
        todayButtonText: "todayButtonText",
        type: "type",
        useMaskBehavior: "useMaskBehavior",
        validationError: "validationError",
        validationErrors: "validationErrors",
        validationMessageMode: "validationMessageMode",
        validationMessagePosition: "validationMessagePosition",
        validationStatus: "validationStatus",
        value: "value",
        valueChangeEvent: "valueChangeEvent",
        visible: "visible",
        width: "width"
      },
      outputs: {
        onChange: "onChange",
        onClosed: "onClosed",
        onContentReady: "onContentReady",
        onCopy: "onCopy",
        onCut: "onCut",
        onDisposing: "onDisposing",
        onEnterKey: "onEnterKey",
        onFocusIn: "onFocusIn",
        onFocusOut: "onFocusOut",
        onInitialized: "onInitialized",
        onInput: "onInput",
        onKeyDown: "onKeyDown",
        onKeyUp: "onKeyUp",
        onOpened: "onOpened",
        onOptionChanged: "onOptionChanged",
        onPaste: "onPaste",
        onValueChanged: "onValueChanged",
        acceptCustomValueChange: "acceptCustomValueChange",
        accessKeyChange: "accessKeyChange",
        activeStateEnabledChange: "activeStateEnabledChange",
        adaptivityEnabledChange: "adaptivityEnabledChange",
        applyButtonTextChange: "applyButtonTextChange",
        applyValueModeChange: "applyValueModeChange",
        buttonsChange: "buttonsChange",
        calendarOptionsChange: "calendarOptionsChange",
        cancelButtonTextChange: "cancelButtonTextChange",
        dateOutOfRangeMessageChange: "dateOutOfRangeMessageChange",
        dateSerializationFormatChange: "dateSerializationFormatChange",
        deferRenderingChange: "deferRenderingChange",
        disabledChange: "disabledChange",
        disabledDatesChange: "disabledDatesChange",
        displayFormatChange: "displayFormatChange",
        dropDownButtonTemplateChange: "dropDownButtonTemplateChange",
        dropDownOptionsChange: "dropDownOptionsChange",
        elementAttrChange: "elementAttrChange",
        focusStateEnabledChange: "focusStateEnabledChange",
        heightChange: "heightChange",
        hintChange: "hintChange",
        hoverStateEnabledChange: "hoverStateEnabledChange",
        inputAttrChange: "inputAttrChange",
        intervalChange: "intervalChange",
        invalidDateMessageChange: "invalidDateMessageChange",
        isDirtyChange: "isDirtyChange",
        isValidChange: "isValidChange",
        labelChange: "labelChange",
        labelModeChange: "labelModeChange",
        maxChange: "maxChange",
        maxLengthChange: "maxLengthChange",
        minChange: "minChange",
        nameChange: "nameChange",
        openedChange: "openedChange",
        openOnFieldClickChange: "openOnFieldClickChange",
        pickerTypeChange: "pickerTypeChange",
        placeholderChange: "placeholderChange",
        readOnlyChange: "readOnlyChange",
        rtlEnabledChange: "rtlEnabledChange",
        showAnalogClockChange: "showAnalogClockChange",
        showClearButtonChange: "showClearButtonChange",
        showDropDownButtonChange: "showDropDownButtonChange",
        spellcheckChange: "spellcheckChange",
        stylingModeChange: "stylingModeChange",
        tabIndexChange: "tabIndexChange",
        textChange: "textChange",
        todayButtonTextChange: "todayButtonTextChange",
        typeChange: "typeChange",
        useMaskBehaviorChange: "useMaskBehaviorChange",
        validationErrorChange: "validationErrorChange",
        validationErrorsChange: "validationErrorsChange",
        validationMessageModeChange: "validationMessageModeChange",
        validationMessagePositionChange: "validationMessagePositionChange",
        validationStatusChange: "validationStatusChange",
        valueChange: "valueChange",
        valueChangeEventChange: "valueChangeEventChange",
        visibleChange: "visibleChange",
        widthChange: "widthChange",
        onBlur: "onBlur"
      },
      features: [i0.ɵɵProvidersFeature([DxTemplateHost, WatcherHelper, CUSTOM_VALUE_ACCESSOR_PROVIDER, NestedOptionHost, IterableDifferHelper]), i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
      decls: 0,
      vars: 0,
      template: function DxDateBoxComponent_Template(rf, ctx) {},
      dependencies: [DxIntegrationModule],
      encapsulation: 2
    });
  }
  return DxDateBoxComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let DxDateBoxModule = /*#__PURE__*/(() => {
  class DxDateBoxModule {
    /** @nocollapse */static ɵfac = function DxDateBoxModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || DxDateBoxModule)();
    };
    /** @nocollapse */
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: DxDateBoxModule
    });
    /** @nocollapse */
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [DxDateBoxComponent, DxiButtonModule, DxoOptionsModule, DxoCalendarOptionsModule, DxoDisplayFormatModule, DxoDropDownOptionsModule, DxoAnimationModule, DxoHideModule, DxoFromModule, DxoPositionModule, DxoAtModule, DxoBoundaryOffsetModule, DxoCollisionModule, DxoMyModule, DxoOffsetModule, DxoToModule, DxoShowModule, DxoDateBoxAnimationModule, DxoDateBoxAtModule, DxoDateBoxBoundaryOffsetModule, DxiDateBoxButtonModule, DxoDateBoxCalendarOptionsModule, DxoDateBoxCollisionModule, DxoDateBoxDisplayFormatModule, DxoDateBoxDropDownOptionsModule, DxoDateBoxFromModule, DxoDateBoxHideModule, DxoDateBoxMyModule, DxoDateBoxOffsetModule, DxoDateBoxOptionsModule, DxoDateBoxPositionModule, DxoDateBoxShowModule, DxoDateBoxToModule, DxiDateBoxToolbarItemModule, DxIntegrationModule, DxTemplateModule, DxiButtonModule, DxoOptionsModule, DxoCalendarOptionsModule, DxoDisplayFormatModule, DxoDropDownOptionsModule, DxoAnimationModule, DxoHideModule, DxoFromModule, DxoPositionModule, DxoAtModule, DxoBoundaryOffsetModule, DxoCollisionModule, DxoMyModule, DxoOffsetModule, DxoToModule, DxoShowModule, DxoDateBoxAnimationModule, DxoDateBoxAtModule, DxoDateBoxBoundaryOffsetModule, DxiDateBoxButtonModule, DxoDateBoxCalendarOptionsModule, DxoDateBoxCollisionModule, DxoDateBoxDisplayFormatModule, DxoDateBoxDropDownOptionsModule, DxoDateBoxFromModule, DxoDateBoxHideModule, DxoDateBoxMyModule, DxoDateBoxOffsetModule, DxoDateBoxOptionsModule, DxoDateBoxPositionModule, DxoDateBoxShowModule, DxoDateBoxToModule, DxiDateBoxToolbarItemModule, DxTemplateModule]
    });
  }
  return DxDateBoxModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { DxDateBoxComponent, DxDateBoxModule };
